import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { RadioCardVariants } from '../../../cdl/RadioCard/components/RadioCard';
import { RadioCardGroup } from '../../../cdl/RadioCard/RadioCardGroup';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { CompanyListing } from '../../../common/CompanyListing/CompanyListing';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { getSortedAccessCompanies } from '../../../common/helpers/getSortedAccessCompanies.helper';
import { identifyUserRole } from '../../../common/helpers/identifyUserRole.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { useManagementUsersPagination } from '../hooks/useManagementUsers';
import { useMemberQuickFilter } from '../hooks/useMemberQuickFilter';

import { MemberDropdown } from './MemberDropdown';
import { MemberNameCell } from './MemberNameCell';
import { NoSearchResultsState } from './NoSearchResultsState';

export const AdminMembersTable = () => {
    const { getHumanReadableValue } = useEnums();

    const search = useSearch({ from: '/_app/members/' });
    const navigate = useNavigate({ from: '/members' });

    const usersPaginationQuery = useManagementUsersPagination({
        companyIds: [...search.supplierIds, ...search.customerIds],
        groupIds: [...search.supplierGroupIds, ...search.customerGroupIds],
        page: search.page,
        searchQuery: search.searchQuery,
        statuses: search.status ? [search.status] : undefined,
    });

    const { selectedFilter, setSelectedFilter, filterOptions } = useMemberQuickFilter({
        searchQuery: search.searchQuery,
        companyIds: [...search.supplierIds, ...search.customerIds],
        groupIds: [...search.supplierGroupIds, ...search.customerGroupIds],
        currentStatus: search.status,
        onStatusChange: (status) => {
            navigate({
                search: (previousSearch) => ({
                    ...previousSearch,
                    status,
                    page: 0,
                }),
            });
        },
    });

    if (usersPaginationQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (usersPaginationQuery.isError) {
        return <AdvancedError />;
    }

    const buildRowLink = (user: UserGatewayMessage) => {
        return linkOptions({
            to: '/members/$id',
            params: {
                id: user.id,
            },
        });
    };

    return (
        <Box display="grid">
            <RadioCardGroup
                options={filterOptions}
                value={selectedFilter}
                onChange={setSelectedFilter}
                variant={RadioCardVariants.SLIM}
            />

            <Box marginTop={16}>
                {!usersPaginationQuery.isPending && usersPaginationQuery.data?.total === 0 ? (
                    <NoSearchResultsState />
                ) : null}

                {usersPaginationQuery.data.content.length > 0 ? (
                    <TableBuilder
                        data={usersPaginationQuery.data?.content || []}
                        rowLink={buildRowLink}
                        grow={false}
                        isLoading={usersPaginationQuery.isPlaceholderData && usersPaginationQuery.isFetching}
                    >
                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.name')} width="20%">
                            {(user) => <MemberNameCell user={user} />}
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.mail')} truncate>
                            {(user) => user.email}
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.group')} width="20%" truncate>
                            {(user) => user.group?.name}
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.userType')} width="96px">
                            {(user) => getHumanReadableValue('userType', user.userType)}
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.role')} width="120px">
                            {(user) =>
                                getHumanReadableValue('role', identifyUserRole(user.accessLevel, user.companyAccesses))
                            }
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header={translate('user.companies')} width="20%">
                            {(user) => {
                                const companies = getSortedAccessCompanies(user.companyAccesses);

                                if (!companies) {
                                    return null;
                                }

                                return <CompanyListing companies={companies} />;
                            }}
                        </TableBuilderColumn>

                        <TableBuilderColumn<UserGatewayMessage> header="" width="64px" interactive>
                            {(user) => <MemberDropdown user={user} />}
                        </TableBuilderColumn>
                    </TableBuilder>
                ) : null}
            </Box>

            {usersPaginationQuery.pageTotal > 1 ? (
                <CenteredPagination
                    currentPage={search.page}
                    pageTotal={usersPaginationQuery.pageTotal}
                    onPageChange={(page) => {
                        navigate({
                            search: (previousSearch) => ({
                                ...previousSearch,
                                page,
                            }),
                        });
                    }}
                />
            ) : null}
        </Box>
    );
};
